import { toZonedTime, format } from "date-fns-tz";

/**
 * transform number to IDR currency
 * @param value number in string | number
 * @param isExplicitSign show positive / negative sign explicitly
 * @returns IDR currency in string
 */
export const toRupiah = (value: string | number, isExplicitSign: boolean = false) => {
  const number = Number(value);
  const currency = new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
    signDisplay: "never",
  }).format(number);
  let sign = "";
  if (number > 0) sign = "+ ";
  else if (number < 0) sign = "- ";
  return `${isExplicitSign ? sign : ""}${currency}`;
};

/**
 * transform IDR currency to number in string
 * @param value IDR currency in string
 * @returns number in string
 */
export const toNumber = (value: string) => value.replace(/[A-Za-z]|,00|[$&+,:;=?@#|'<>.^*()%!-/\\{}_[\]]|\s/g, "");

const parseToUTCISOString = (input: string) => {
  let year;
  let month;
  let day;
  let hour;
  let minute;
  let second;

  // Normalize separator
  const clean = input.replace(/[-/]/g, "/");
  const [datePart, timePart] = clean.split(" ");

  const timeSegments = timePart?.split(":").map(String) ?? [];

  // Format 1 & 2: "07/01/2025 00:00:00" => MM/dd/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(datePart)) {
    const [mm, dd, yyyy] = datePart.split("/").map(String);
    [year, month, day] = [yyyy, mm, dd];
  } else if (/^\d{4}\/\d{2}\/\d{2}$/.test(datePart)) {
    // Format 3: "2025/07/01 04:25:52" => yyyy/MM/dd
    const [yyyy, mm, dd] = datePart.split("/").map(String);
    [year, month, day] = [yyyy, mm, dd];
  } else {
    return undefined;
  }

  if (timeSegments.length === 3) {
    [hour, minute, second] = timeSegments;
  }

  const localDate = `${year}-${month}-${day}T${hour}:${minute}:${second}Z`;

  return localDate;
};

/**
 * check if value is Date object or not
 * @param value value any, to be checked
 * @returns Date if true, undefined if false
 */
export const getDateIfValid = (date: any) => (
  date instanceof Date && !Number.isNaN(Number(date))
    ? date
    : undefined
);

/**
 * transform date to formatted date (DD MMMM YYYY)
 * @param value date in string
 * @param withTime boolean
 * @param withSecond boolean
 * @returns formatted date (DD MMMM YYYY)
 */
export const toStringDate = (value?: string, withTime?: boolean, withSecond?: boolean) => {
  // eslint-disable-next-line no-nested-ternary
  const date = value && parseToUTCISOString(value)
    ? new Date(parseToUTCISOString(value) || value)
    : (
      value
        ? new Date(value)
        : undefined
    );

  const validDate = getDateIfValid(date);

  const stringDate = validDate
    ? validDate.toLocaleString("id-ID", {
      day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta",
    })
    : "";

  const stringTime = validDate
    ? validDate.toLocaleString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      second: withSecond ? "2-digit" : undefined,
      // timeZone: "Asia/Jakarta",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    : "";

  return `${stringDate}${withTime && stringTime ? `, ${stringTime}` : ""}`;
};

/**
 * check if a string only contains number characters
 * @param value string
 * @returns boolean
 */
export const isNumber = (value: string) => (/^[0-9]+$/).test(value);

/**
 * generate error object for Kitbird's Form
 * @param error { message: string }
 * @returns error object
 */
export const generateErrorObj = (error: { message?: string | undefined } | undefined) => (
  error
    ? ({ message: JSON.stringify(error.message) })
    : undefined
);

/**
 * transform file size (byte) to string
 * @param size number
 * @param staticUnit "B" | "KB" | "MB" | "GB" | undefined
 * @param isRound boolean | undefined
 * @returns string
 */
export const byteToString = (size: number, staticUnit?: "B" | "KB" | "MB" | "GB" | "none", isRound?: boolean) => {
  let value = size;
  let unit = staticUnit || "B";
  switch (staticUnit) {
    case "GB":
      value /= (1024 * 1024 * 1024);
      unit = "GB";
      break;
    case "MB":
      value /= (1024 * 1024);
      unit = "MB";
      break;
    case "KB":
      value /= (1024);
      unit = "KB";
      break;
    case undefined:
      if (size >= 1024 * 1024 * 1024) {
        value /= (1024 * 1024 * 1024);
        unit = "GB";
      } else if (size >= 1024 * 1024) {
        value /= (1024 * 1024);
        unit = "MB";
      } else if (size >= 1024) {
        value /= (1024);
        unit = "KB";
      }
      break;

    default:
      break;
  }
  if (isRound !== false) value = Math.round(value);
  return staticUnit === "none" ? `${value}` : `${value} ${unit}`;
};

/**
 * transform Byte string to string
 * @param size file size in Byte string (e.g "12040 B")
 * @returns file size string
 */
export const byteStringToFileSize = (size: string) => {
  let fileSize = size;
  const splitSize = size.split(" B");
  const sizeInNumber = Number(splitSize[0]);
  if (splitSize.length === 2 && !Number.isNaN(sizeInNumber)) {
    fileSize = byteToString(sizeInNumber);
  }
  return fileSize;
};

/**
 * Checking if a string is HTML string or not
 * @param str string
 * @returns boolean
 */
export const isHtmlString = (str: string) => /<(?=.*? .*?\/ ?>|br|hr|input|!--|wbr)[a-z]+.*?>|<([a-z]+).*?<\/\1>/i.test(str);

/**
 * transform date to string date with YYYY-MM-DD format
 * @param date Date
 * @returns string
 */
export const dateToFormatedStringDate = (date?: Date) => (date
  ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`
  : undefined
);

/**
 * transform string to capital on first letter and lower case on the rest
 * @param string String
 * @returns string
 */
export const capitalizeFirstLetter = (string: string) => (
  string.charAt(0).toUpperCase()
  + string.slice(1).toLowerCase()
);

/**
 * Converts a UTC date string to a specified timezone and format.
 *
 * @param {string} utcDateString - The date string from the API (e.g., "10/01/2025 03:47:02").
 * @param {string} [outputFormat="dd/MM/yyyy, HH:mm"] - Optional format string for the output.
 * @returns {string} The formatted date string in GMT+7 (e.g., "01/10/2025, 10:47")
 * or an error message.
 */
export const formatUTCDate = (
  utcDateString: string,
  outputFormat: string = "dd/MM/yyyy, HH:mm",
) => {
  try {
    const dateISOString = new Date(String(parseToUTCISOString(utcDateString)));
    const targetTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Jakarta";
    const zonedDate = toZonedTime(dateISOString, targetTimeZone);
    return format(zonedDate, outputFormat, { timeZone: targetTimeZone });
  } catch (error) {
    console.error("Failed to format date:", error);
    return "Invalid Date";
  }
};
